import FormDialog from 'login-components/Common/Form/FormDialog/FormDialog';
import autobind from 'autobind-decorator';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import * as React from 'react';
import AppDocumentTitle from 'login-components/Common/AppDocumentTitle/AppDocumentTitle';
import AppTitleUpdater from 'login-components/Common/AppTitleUpdater';
import { setHref, WindowWrapper } from 'login-utils/globalWrappers';
import _ from 'lodash';
import baseRequest from 'login-apis/baseRequest';
import RoleSelector from '../RoleSelector/RoleSelector';
import WorkflowSelector from '../WorkflowSelector/WorkflowSelector';
import ApplicationDetailsForm from '../ApplicationDetailsForm/ApplicationDetailsForm';
import FastForwardOptions from '../FastForwardOptions/FastForwardOptions';
import SectionLabel from '../SectionLabel/SectionLabel';
import * as fastForwardUtils from '../fastForwardUtils';
import './FastForwardContainer.scss';

const borrowerTypeStrings: string[] = _.map(fastForwardUtils.borrowerTypes, bt => bt.text);
const coborrowerOptionStrings: string[] = _.map(fastForwardUtils.coborrowerOptions, co => co.text);
const dataSharingOptionStrings: string[] = _.map(fastForwardUtils.dataSharingOptions, mt => mt.text);
const mortgageTypeStrings: string[] = _.map(fastForwardUtils.getMortgageLoanPurposeTypes(), mt => mt.text);
const BUILT_IN_WORKFLOWS = 'Built-in Workflows';
const CUSTOM_WORKFLOWS = 'Custom Workflows';
const builtInWorkflowLists = [BUILT_IN_WORKFLOWS, CUSTOM_WORKFLOWS];

interface FastForwardContainerState extends FastForwardApplicationConfig {
  borrowerPassword: string;
  roleError?: string | null;
  ffError?: string | null;
  loadingMessage?: string | null;
  selectedWorkflowList: string;
}

function getLoanType(loanTypes: ILoanType[], stateLoanType: string) {
  return _.find(loanTypes, { text: stateLoanType })!.value;
}

@autobind
class FastForwardContainer extends React.Component<{}, FastForwardContainerState> {
  fakeLogin = fastForwardUtils.getFakeLogin();
  loanTypes = fastForwardUtils.getLoanTypes();
  workflows = fastForwardUtils.getWorkflows();
  appTemplates = fastForwardUtils.getAppTemplates();

  constructor(props) {
    super(props);
    this.state = {
      borrowerType: fastForwardUtils.borrowerTypes[0].text,
      coborrowerOption: fastForwardUtils.coborrowerOptions[0].text,
      dataSharingOption: fastForwardUtils.dataSharingOptions[0].text,
      mortgageType: fastForwardUtils.getMortgageLoanPurposeTypes()[0].text,
      loanType: this.loanTypes[0].text,
      borrowerEmail: this.fakeLogin.username,
      borrowerPassword: this.fakeLogin.password,
      applicationTemplateId: undefined,
      roleError: null,
      ffError: null,
      loadingMessage: null,
      selectedWorkflowList: BUILT_IN_WORKFLOWS,
    };
  }

  async submitRole(role) {
    this.setState({
      loadingMessage: `Creating user for role ${role}`,
    } as FastForwardContainerState);

    try {
      await baseRequest.post(`/api/public/fast-forward/${role}`);
      window.location.assign('/');
    } catch (res) {
      this.setState({
        roleError: res.response.data,
        loadingMessage: null,
      } as FastForwardContainerState);
    }
  }

  updateBorrowerState(update) {
    this.setState(update);
  }

  async selectWorkflow(requestId: string, milestoneId: string, appTemplateId: string, followUpType: string) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const {
      borrowerType,
      coborrowerOption,
      mortgageType,
      dataSharingOption,
      borrowerEmail,
      borrowerPassword,
      loanType: stateLoanType,
    } = this.state;
    const borrowerConfig = _.find(fastForwardUtils.borrowerTypes, { text: borrowerType })!.coborrowerExists;
    const loanType = getLoanType(this.loanTypes, stateLoanType);

    const workflowCreationOpts = {
      username: borrowerEmail,
      password: borrowerPassword,
      loanType,
      ffOpts: {
        borrowersMarried:
          borrowerConfig && _.find(fastForwardUtils.coborrowerOptions, { text: coborrowerOption })!.borrowersMarried,
      },
    };

    if (!followUpType) {
      _.assign(workflowCreationOpts.ffOpts, { selectedRequest: requestId });
      if (appTemplateId) {
        _.assign(workflowCreationOpts, { applicationTemplateId: appTemplateId });
      } else {
        // "post sub" hack uses empty requestId, filter it out if need be
        _.assign(workflowCreationOpts.ffOpts, {
          requirementConfigs: _.filter([{ requestId, milestoneId }], 'requestId'),
        });
      }
    } else {
      _.assign(workflowCreationOpts.ffOpts, {
        followUpOnly: true,
        followUps: [{ type: followUpType }],
      });
    }

    if (borrowerConfig) {
      _.extend(workflowCreationOpts.ffOpts, {
        coborrower: {
          restrictedData: _.find(fastForwardUtils.dataSharingOptions, { text: dataSharingOption })!.restrictedData,
          email: fastForwardUtils.getFakeLogin().username,
        },
      });
    }

    if (loanType === 'MORTGAGE') {
      _.extend(workflowCreationOpts.ffOpts, {
        loanPurposeType: _.find(fastForwardUtils.getMortgageLoanPurposeTypes(), { text: mortgageType })!
          .loanPurposeType,
      });
    }

    this.setState({
      loadingMessage: 'Concocting magic sauce, will be just a moment...',
    });

    try {
      await baseRequest.post('/api/public/ff', workflowCreationOpts).then(() => {
        // Slight hack because it depends on angular app's URL structure, but E2Es will fail loudly if this changes,
        // so it's an okay one
        setHref(`/section/${requestId}/interstitial`);
      });
    } catch (res) {
      this.setState({
        ...self.state,
        ffError: res.response.data,
        loadingMessage: null,
      });
    }
  }

  setCustom(e) {
    const { value } = e.currentTarget;
    this.setState({ selectedWorkflowList: value });
  }

  render() {
    const {
      borrowerType,
      borrowerEmail,
      coborrowerOption,
      dataSharingOption,
      mortgageType,
      loanType,
      selectedWorkflowList,
      loadingMessage,
      roleError,
      ffError,
    } = this.state;

    const title = 'Dev Tools';
    const [customWorkflows, builtInWorkflows] = _.partition(this.workflows, 'custom');
    const loanTypeValue = getLoanType(this.loanTypes, loanType);
    const workflowLists = _.concat(
      builtInWorkflowLists,
      _(this.appTemplates)
        .filter(({ loanType: appTemplateLoanType }) => appTemplateLoanType === loanTypeValue)
        .map(appTemplate => appTemplate.name)
        .sortBy()
        .value(),
    );

    const workflowList = (() => {
      if (selectedWorkflowList === BUILT_IN_WORKFLOWS) return builtInWorkflows;
      else if (selectedWorkflowList === CUSTOM_WORKFLOWS) return customWorkflows;

      const appTemplate = _.find(this.appTemplates, { name: selectedWorkflowList })!;
      return _.map(appTemplate.requirementConfigs, ({ requestId, milestoneId }) => ({
        appTemplateId: appTemplate._id,
        requestId,
        text: requestId,
        validMilestoneIds: [milestoneId],
      }));
    })();

    return (
      <div className="FastForwardContainer">
        <AppTitleUpdater text={title}>
          <AppDocumentTitle title={title}>
            <FormDialog>
              <div className="FastForwardContainer-contentWrapper">
                {!loadingMessage && (
                  <div>
                    {_.includes(['dev', 'sandbox'], WindowWrapper.NODE_ENV) && (
                      <div>
                        <div>{roleError && <ErrorMessage errorText={roleError} />}</div>
                        <div className="FastForwardContainer-componentWrapper">
                          <RoleSelector data-it="RoleSelector" submitRole={this.submitRole} />
                        </div>
                      </div>
                    )}
                    {ffError && <ErrorMessage errorText={ffError} />}
                    <SectionLabel text="Fast Forward" />
                    <div className="FastForwardContainer-componentWrapper">
                      <ApplicationDetailsForm
                        borrowerTypes={borrowerTypeStrings}
                        borrowerType={borrowerType}
                        borrowerEmail={borrowerEmail}
                        coborrowerOptions={coborrowerOptionStrings}
                        coborrowerOption={coborrowerOption}
                        dataSharingOptions={dataSharingOptionStrings}
                        dataSharingOption={dataSharingOption}
                        mortgageTypes={mortgageTypeStrings}
                        mortgageType={mortgageType}
                        loanTypes={this.loanTypes}
                        loanType={loanType}
                        expanded={false}
                        onChange={this.updateBorrowerState}
                      />
                    </div>
                    <div className="FastForwardContainer-divider" />
                    <div className="SubsectionLabel"> Select Workflows </div>
                    <FastForwardOptions
                      options={workflowLists}
                      optionName="selectedWorkflowList"
                      defaultValue="Built-in Workflows"
                      title="Application Templates"
                      onChange={this.setCustom}
                    />

                    <div className="FastForwardContainer-componentWrapper">
                      <WorkflowSelector workflows={workflowList} selectWorkflow={this.selectWorkflow} />
                    </div>
                  </div>
                )}
                {loadingMessage && <h1 className="FastForwardContainer-loadingMessage">{loadingMessage}</h1>}
              </div>
            </FormDialog>
          </AppDocumentTitle>
        </AppTitleUpdater>
      </div>
    );
  }
}

export default FastForwardContainer;
